import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnChanges,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  catchError,
  EMPTY,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import {
  DepartmentDetails,
  DepartmentPosition,
  DepartmentUserRow,
} from '../../department-model';
import { DepartmentService } from '../../department.service';
import { DepartmentPositionCardComponent } from '../../department-position-card/department-position-card.component';
import { SectionHeaderComponent } from '../../../../../../common/components/section-header/section-header.component';
import { ADMIN_ROUTES } from '../../../../../../common/constants/admin.routing.constants';
import { TenantPathPipe } from '../../../../../../common/pipes/tenant-path/tenant-path.pipe';
import { AvatarPipe } from '../../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { LoaderComponent } from '../../../../../../../ui-components/lib/loader/loader.component';
import { UserDropdownViewComponent } from '../../../../../../../ui-components/lib/user-dropdown-view/user-dropdown-view.component';

export const QUICK_VIEW_POSITIONS_LIMIT = 6;
export const QUICK_VIEW_USERS_PAGE_SIZE = 20;
export const QUICK_VIEW_PREVIEW_USERS = 5;

type QuickViewSection = 'info' | 'users' | 'positions';
type RetryableSection = QuickViewSection | 'usersList';
type UsersListStatus = 'idle' | 'loading' | 'error';

interface SectionState<T> {
  status: 'loading' | 'loaded' | 'error';
  data: T;
}

interface UsersPage {
  users: DepartmentUserRow[];
  page: number;
  lastPage: number;
  total: number;
}

interface UsersMenuRequest {
  page: number;
  search: string;
}

interface DropdownUser {
  id: number;
  name: string;
  image: DepartmentUserRow['image'];
  job_title: string;
}

function toDropdownUser(user: DepartmentUserRow): DropdownUser {
  return {
    id: user.id,
    name: user.name,
    image: user.image,
    job_title: user.position?.job_title?.name ?? '',
  };
}

const EMPTY_USERS_PAGE: UsersPage = {
  users: [],
  page: 1,
  lastPage: 1,
  total: 0,
};

@Component({
  selector: 'lib-department-quick-view',
  imports: [
    NgTemplateOutlet,
    RouterLink,
    TranslocoPipe,
    DepartmentPositionCardComponent,
    SectionHeaderComponent,
    TenantPathPipe,
    AvatarPipe,
    LoaderComponent,
    UserDropdownViewComponent,
  ],
  templateUrl: './department-quick-view.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentQuickViewComponent implements OnChanges {
  private readonly departmentService = inject(DepartmentService);
  private readonly destroyRef = inject(DestroyRef);

  readonly departmentId = input.required<number>();

  protected readonly details = signal<SectionState<DepartmentDetails | null>>({
    status: 'loading',
    data: null,
  });
  protected readonly positions = signal<SectionState<DepartmentPosition[]>>({
    status: 'loading',
    data: [],
  });
  protected readonly usersPreview = signal<SectionState<UsersPage>>({
    status: 'loading',
    data: EMPTY_USERS_PAGE,
  });
  protected readonly previewUserLimit = QUICK_VIEW_PREVIEW_USERS;
  protected readonly departmentKeys = computed(() => [this.departmentId()]);
  protected readonly dropdownUsers = signal<DepartmentUserRow[]>([]);
  protected readonly dropdownPage = signal(1);
  protected readonly dropdownLastPage = signal(1);
  protected readonly dropdownTotal = signal(0);
  protected readonly usersListStatus = signal<UsersListStatus>('idle');

  protected readonly info = computed(() => {
    const department = this.details().data;
    const parent = department?.parent;
    const manager = department?.direct_manager;
    return {
      legalEntity: department?.legal_entity?.name ?? null,
      parent: parent
        ? {
            name: parent.name || parent.label,
            imageUrl: parent.image?.url ?? null,
            detailsPath: parent.value
              ? `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${parent.value}`
              : null,
          }
        : null,
      manager: manager
        ? {
            name: manager.name || manager.label,
            imageUrl: manager.image?.url ?? null,
          }
        : null,
      type: department?.department_type?.label ?? null,
    };
  });
  protected readonly previewUsers = computed(() =>
    this.usersPreview().data.users.map(toDropdownUser)
  );
  protected readonly dropdownViewUsers = computed(() =>
    this.dropdownUsers().map(toDropdownUser)
  );
  protected readonly detailsPath = computed(
    () => `${ADMIN_ROUTES.DEPARTMENTS.DETAILS}/${this.departmentId()}`
  );
  protected readonly editPath = computed(
    () => `${ADMIN_ROUTES.DEPARTMENTS.FORM}/${this.departmentId()}`
  );

  private readonly departmentChanges$ = new Subject<number>();
  private readonly retry$ = {
    info: new Subject<void>(),
    users: new Subject<void>(),
    positions: new Subject<void>(),
  };
  private readonly usersMenu$ = new Subject<UsersMenuRequest>();
  private failedUsersRequest: UsersMenuRequest | null = null;

  constructor() {
    this.departmentChanges$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.dropdownUsers.set([]);
      this.dropdownPage.set(1);
      this.dropdownLastPage.set(1);
      this.dropdownTotal.set(0);
      this.usersListStatus.set('idle');
      this.failedUsersRequest = null;
    });

    this.bindSection(
      this.retry$.info,
      id =>
        this.departmentService
          .getDepartment(String(id))
          .pipe(map(response => response.data)),
      this.details,
      null
    );
    this.bindSection(
      this.retry$.positions,
      id =>
        this.departmentService
          .getDepartmentPositions(String(id), 1, QUICK_VIEW_POSITIONS_LIMIT)
          .pipe(map(response => response.data)),
      this.positions,
      []
    );
    this.bindSection(
      this.retry$.users,
      id => this.requestUsers(id, 1, ''),
      this.usersPreview,
      EMPTY_USERS_PAGE
    );

    this.departmentChanges$
      .pipe(
        switchMap(departmentId =>
          this.usersMenu$.pipe(
            switchMap(request => this.usersMenuPage(departmentId, request))
          )
        ),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['departmentId']) {
      this.departmentChanges$.next(this.departmentId());
    }
  }

  retry(section: RetryableSection): void {
    if (section === 'usersList') {
      if (this.failedUsersRequest) {
        this.usersMenu$.next(this.failedUsersRequest);
      }
      return;
    }

    this.retry$[section].next();
  }

  loadUsersMenu(event: { currentPage: number; search: string }): void {
    this.usersMenu$.next({ page: event.currentPage, search: event.search });
  }

  /**
   * Opening the menu with an empty search shows the preview page. Any other
   * request replaces that page, and a later page is appended. A new request
   * cancels the one still in flight.
   */
  private usersMenuPage(
    departmentId: number,
    request: UsersMenuRequest
  ): Observable<UsersPage> {
    const preview = this.usersPreview();
    if (
      request.page === 1 &&
      request.search === '' &&
      preview.status === 'loaded'
    ) {
      this.dropdownUsers.set(preview.data.users);
      this.dropdownPage.set(preview.data.page);
      this.dropdownLastPage.set(preview.data.lastPage);
      this.dropdownTotal.set(preview.data.total);
      this.usersListStatus.set('idle');
      this.failedUsersRequest = null;
      return EMPTY;
    }

    this.usersListStatus.set('loading');
    return this.requestUsers(departmentId, request.page, request.search).pipe(
      tap(next => {
        this.dropdownUsers.set(
          request.page === 1
            ? next.users
            : [...this.dropdownUsers(), ...next.users]
        );
        this.dropdownPage.set(next.page);
        this.dropdownLastPage.set(next.lastPage);
        this.dropdownTotal.set(next.total);
        this.usersListStatus.set('idle');
        this.failedUsersRequest = null;
      }),
      catchError(() => {
        this.failedUsersRequest = request;
        if (request.page === 1) {
          this.dropdownUsers.set([]);
          this.dropdownTotal.set(0);
        }
        this.usersListStatus.set('error');
        return EMPTY;
      })
    );
  }

  private requestUsers(
    departmentId: number,
    page: number,
    search: string
  ): Observable<UsersPage> {
    return this.departmentService
      .getDepartmentUsers(
        String(departmentId),
        page,
        search,
        QUICK_VIEW_USERS_PAGE_SIZE
      )
      .pipe(
        map(response => ({
          users: response.data,
          page: response.meta.current_page,
          lastPage: response.meta.last_page,
          total: response.meta.total,
        }))
      );
  }

  private bindSection<T>(
    retry$: Observable<void>,
    request: (departmentId: number) => Observable<T>,
    target: WritableSignal<SectionState<T>>,
    empty: T
  ): void {
    merge(this.departmentChanges$, retry$.pipe(map(() => this.departmentId())))
      .pipe(
        switchMap(departmentId =>
          request(departmentId).pipe(
            map((data): SectionState<T> => ({ status: 'loaded', data })),
            catchError(() =>
              of<SectionState<T>>({ status: 'error', data: empty })
            ),
            startWith<SectionState<T>>({ status: 'loading', data: empty })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(state => target.set(state));
  }
}
